import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import {
    getAssetsBoard,
    encerrarPosicoes,
    encerrarPosicoesUser,
    retomarPredador,
    retomarPredadorUser
} from '../../services/UsersService';
import './GerenciarAtivos.css';

const DEFAULT_MODES = [
    { value: 'top_hunter', label: 'Predador' },
    { value: 'technical_signal', label: 'Predador 2' },
    { value: 'market_confluence', label: 'Predador 3' },
    { value: 'buy_only', label: 'Predador 4' }
];

function formatUsd(value, signed = false) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0,00';
    const formatted = number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    if (!signed) return formatted;
    if (number > 0) return `+${formatted}`;
    return formatted;
}

function formatPerc(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '—';
    const formatted = number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    if (number > 0) return `+${formatted}%`;
    return `${formatted}%`;
}

function tone(value) {
    const number = Number(value);
    if (number > 0) return 'up';
    if (number < 0) return 'down';
    return '';
}

function apiError(err) {
    const raw = err?.response ? err.response.data : err?.message;
    if (typeof raw === 'string') return raw;
    if (raw && raw.message) return raw.message;
    return raw ? JSON.stringify(raw) : 'Não foi possível concluir a operação.';
}

function GerenciarAtivos() {
    const [board, setBoard] = useState({ users: [], totals: {}, huntModes: DEFAULT_MODES, paused: false });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');
    const [allHuntMode, setAllHuntMode] = useState('top_hunter');
    const [userModes, setUserModes] = useState({});
    const [notification, setNotification] = useState({ type: '', text: '' });

    const huntModes = board.huntModes?.length ? board.huntModes : DEFAULT_MODES;
    const users = board.users || [];
    const totals = board.totals || {};

    const ranked = useMemo(
        () => [...users].sort((a, b) => {
            const aOpen = Number(a.cryptoCount) > 0 ? 1 : 0;
            const bOpen = Number(b.cryptoCount) > 0 ? 1 : 0;
            if (bOpen !== aOpen) return bOpen - aOpen;
            const byValue = (Number(b.cryptoUsdc) || 0) - (Number(a.cryptoUsdc) || 0);
            if (byValue) return byValue;
            return String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''), 'pt-BR');
        }),
        [users]
    );
    const positionCount = Number(totals.positionCount) || 0;
    const userCount = Number(totals.userCount) || users.length;
    const activeCount = Number(totals.activeCount) || users.filter(user => Number(user.cryptoCount) > 0).length;

    function loadBoard() {
        setLoading(true);
        getAssetsBoard()
            .then(result => {
                setBoard(result || { users: [], totals: {}, huntModes: DEFAULT_MODES });
                const next = {};
                (result?.users || []).forEach(user => {
                    next[user.id] = user.huntMode || 'top_hunter';
                });
                setUserModes(next);
                if (!allHuntMode && result?.huntModes?.[0]?.value)
                    setAllHuntMode(result.huntModes[0].value);
            })
            .catch(err => {
                setNotification({ type: 'error', text: apiError(err) });
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        loadBoard();
    }, []);

    async function run(key, successText, action) {
        if (busy) return;
        setBusy(key);
        try {
            const result = await action();
            setNotification({
                type: 'success',
                text: result?.message || successText
            });
            loadBoard();
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setBusy('');
        }
    }

    function onEncerrarTodos() {
        if (!positionCount) return;
        const ok = window.confirm(
            `Encerrar as ${positionCount} posição(ões) abertas do Predador?\n\n` +
            'Vende só o que está Em posição no Predador Status. Clientes sem ativos ficam de fora.'
        );
        if (!ok) return;
        run('all-close', 'Posições do Predador encerradas.', () => encerrarPosicoes());
    }

    function onRetomarTodos() {
        const label = huntModes.find(mode => mode.value === allHuntMode)?.label || 'Predador';
        const ok = window.confirm(`Retomar todos com ${label}?`);
        if (!ok) return;
        run('all-resume', `${label} retomado para todos.`, () => retomarPredador(allHuntMode));
    }

    function onEncerrarUser(user) {
        if (!Number(user.cryptoCount)) return;
        const pairs = (user.positions || [])
            .map(position => String(position.symbol || '').replace(/USDC$/, '/USDC'))
            .join(', ');
        const ok = window.confirm(
            `Encerrar as posições de ${user.name || user.email}?\n\n` +
            `${pairs || 'Nenhum par listado'}\n\n` +
            'Vende só o que está Em posição no Predador Status. Saldo parado não entra.'
        );
        if (!ok) return;
        run(`close-${user.id}`, `Posições encerradas para ${user.name || user.email}.`, () =>
            encerrarPosicoesUser(user.id)
        );
    }

    function onRetomarUser(user) {
        const huntMode = userModes[user.id] || user.huntMode || 'top_hunter';
        const label = huntModes.find(mode => mode.value === huntMode)?.label || 'Predador';
        const ok = window.confirm(`Retomar ${user.name || user.email} com ${label}?`);
        if (!ok) return;
        run(`resume-${user.id}`, `${label} retomado.`, () => retomarPredadorUser(user.id, huntMode));
    }

    return (
        <>
            <Menu />
            <main className="content assets-page">
                <div className="assets-container">
                    <section className="assets-hero">
                        <div>
                            <span className="assets-eyebrow">Custódia</span>
                            <h1>Gerenciar ativos</h1>
                            <p>
                                Todos os clientes de custódia aparecem aqui. Quem não tem moeda Em posição no Predador Status
                                entra como sem ativos. Encerrar vende só as posições abertas
                                (10% comissão + 10% fundo do lucro da venda) e pausa a caça.
                            </p>
                        </div>
                        <div className="assets-hero-actions">
                            <button
                                type="button"
                                className="assets-danger-btn"
                                disabled={Boolean(busy) || !positionCount}
                                onClick={onEncerrarTodos}
                            >
                                {busy === 'all-close' ? 'Encerrando...' : 'Encerrar posições de todos'}
                            </button>
                            <select
                                className="assets-select"
                                value={allHuntMode}
                                onChange={event => setAllHuntMode(event.target.value)}
                                disabled={Boolean(busy)}
                            >
                                {huntModes.map(mode => (
                                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="assets-success-btn"
                                disabled={Boolean(busy)}
                                onClick={onRetomarTodos}
                            >
                                {busy === 'all-resume' ? 'Retomando...' : 'Retomar todos'}
                            </button>
                            <button
                                type="button"
                                className="assets-ghost-btn"
                                disabled={Boolean(busy) || loading}
                                onClick={loadBoard}
                            >
                                {loading ? 'Atualizando...' : 'Atualizar'}
                            </button>
                        </div>
                    </section>

                    <section className="assets-stats-grid">
                        <div className="assets-stat-card">
                            <small>Clientes</small>
                            <strong>{userCount}</strong>
                            <small>{activeCount} em posição</small>
                        </div>
                        <div className="assets-stat-card">
                            <small>Posições abertas</small>
                            <strong>{positionCount}</strong>
                        </div>
                        <div className="assets-stat-card">
                            <small>Valor em posição</small>
                            <strong>{formatUsd(totals.cryptoUsdc)} USDC</strong>
                        </div>
                        <div className={`assets-stat-card ${tone(totals.pnl)}`}>
                            <small>Lucro aberto</small>
                            <strong>{formatUsd(totals.pnl, true)} USDC · {formatPerc(totals.pnlPerc)}</strong>
                        </div>
                    </section>

                    <section className="assets-market-card">
                        <div className="assets-section-header">
                            <div>
                                <h2>Clientes e posições</h2>
                                <p className="assets-muted">
                                    Quem está Em posição no Predador Status mostra os pares. Os demais aparecem como sem ativos.
                                </p>
                            </div>
                            {board.paused
                                ? <span className="assets-badge warn">Predador global pausado</span>
                                : <span className="assets-badge">Caça liberada</span>}
                        </div>

                        {loading && !ranked.length
                            ? <div className="assets-empty">Carregando ativos...</div>
                            : null}

                        {!loading && !ranked.length
                            ? <div className="assets-empty">Nenhum cliente de custódia encontrado.</div>
                            : null}

                        <div className="assets-pool-grid">
                            {ranked.map(user => (
                                <article
                                    key={user.id}
                                    className={`assets-pool-card${user.huntPaused ? ' paused' : ''}${Number(user.cryptoCount) ? '' : ' empty'}`}
                                >
                                    <div className="assets-card-header">
                                        <div>
                                            <strong>
                                                <Link to={`/users/${user.id}`} className="assets-user-link">
                                                    {user.name || 'Sem nome'}
                                                </Link>
                                            </strong>
                                            <small>{user.email}</small>
                                        </div>
                                        <div className="assets-badges">
                                            <span className="assets-badge">{user.huntLabel}</span>
                                            <span className={`assets-badge${user.huntPaused ? ' warn' : ''}`}>
                                                {user.huntPaused ? 'Pausado' : 'Ativo'}
                                            </span>
                                            {Number(user.cryptoCount)
                                                ? null
                                                : <span className="assets-badge muted">sem ativos</span>}
                                        </div>
                                    </div>
                                    {Number(user.cryptoCount)
                                        ? (
                                            <div className="assets-metrics">
                                                <div>
                                                    <small>Em posição</small>
                                                    <strong>{formatUsd(user.cryptoUsdc)} USDC</strong>
                                                    <small>{`${user.cryptoCount} par(es)`}</small>
                                                </div>
                                                <div>
                                                    <small>Entrada</small>
                                                    <strong>{formatUsd(user.entryUsdc)} USDC</strong>
                                                </div>
                                                <div>
                                                    <small>Lucro aberto</small>
                                                    <strong className={tone(user.pnl)}>{formatUsd(user.pnl, true)} USDC</strong>
                                                </div>
                                                <div>
                                                    <small>Margem</small>
                                                    <strong className={tone(user.pnlPerc)}>{formatPerc(user.pnlPerc)}</strong>
                                                </div>
                                            </div>
                                        )
                                        : null}
                                    {(user.positions || []).length
                                        ? (
                                            <ul className="assets-positions">
                                                {user.positions.map(position => (
                                                    <li key={position.id} className="assets-position-row">
                                                        <span>
                                                            <strong>{String(position.symbol || '').replace(/USDC$/, '/USDC')}</strong>
                                                            {position.buyOnly
                                                                ? <small> Predador 4</small>
                                                                : <small> Predador</small>}
                                                        </span>
                                                        <span className={tone(position.pnl)}>
                                                            {formatUsd(position.currentUsdc)} · {formatPerc(position.pnlPerc)}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )
                                        : <div className="assets-no-assets">sem ativos</div>}
                                    <div className="assets-card-actions">
                                        <button
                                            type="button"
                                            className="assets-danger-btn"
                                            disabled={Boolean(busy) || !Number(user.cryptoCount)}
                                            onClick={() => onEncerrarUser(user)}
                                        >
                                            {busy === `close-${user.id}` ? 'Encerrando...' : 'Encerrar posições'}
                                        </button>
                                        <div className="assets-resume-row">
                                            <select
                                                className="assets-select"
                                                value={userModes[user.id] || user.huntMode || 'top_hunter'}
                                                disabled={Boolean(busy)}
                                                onChange={event => setUserModes(current => ({
                                                    ...current,
                                                    [user.id]: event.target.value
                                                }))}
                                            >
                                                {huntModes.map(mode => (
                                                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="assets-success-btn"
                                                disabled={Boolean(busy)}
                                                onClick={() => onRetomarUser(user)}
                                            >
                                                {busy === `resume-${user.id}` ? '...' : 'Retomar'}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>
                </div>
                <Footer />
            </main>
            {notification.text
                ? <Toast type={notification.type} text={notification.text} />
                : null}
        </>
    );
}

export default GerenciarAtivos;
